import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, ArrowRight, MessageCircle, Zap, X,
  FileText, Code, Image, Mic, Brain, Sparkles,
  Clock, Users, TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../../components/Navigation";

const WHATSAPP_LINK = "https://wa.me/33644692901?text=Bonjour%20!%20Je%20souhaite%20une%20formation%20ChatGPT%20pour%20mon%20entreprise.";

const features = [
  {
    icon: FileText,
    title: "Rédaction & Contenu",
    description: "Emails, rapports, présentations, articles... ChatGPT rédige en quelques secondes ce qui prenait des heures.",
    examples: ["Emails professionnels", "Comptes-rendus de réunion", "Articles de blog", "Descriptions produits"]
  },
  {
    icon: Code,
    title: "Code & Automatisation",
    description: "Génération, correction et explication de code dans tous les langages.",
    examples: ["Scripts Excel/VBA", "Formules complexes", "Automatisation Python", "Requêtes SQL"]
  },
  {
    icon: Brain,
    title: "Analyse & Synthèse",
    description: "Résumez des documents de 100 pages en quelques points clés.",
    examples: ["Synthèse de rapports", "Analyse de contrats", "Veille concurrentielle", "Extraction d'insights"]
  },
  {
    icon: Sparkles,
    title: "Custom GPTs",
    description: "Créez des assistants IA sur mesure pour vos tâches récurrentes.",
    examples: ["Assistant RH", "Bot support client", "Générateur de devis", "Analyste données"]
  }
];

const plans = [
  {
    name: "ChatGPT Free",
    price: "Gratuit",
    features: ["GPT-4o mini", "Limite de messages/jour", "Pas de plugins", "Pas de GPTs personnalisés"],
    recommended: false
  },
  {
    name: "ChatGPT Plus",
    price: "20€/mois",
    features: ["GPT-4o complet", "Messages illimités", "DALL-E 3 inclus", "Custom GPTs", "Browsing web", "Code interpreter"],
    recommended: true
  },
  {
    name: "ChatGPT Team",
    price: "25€/user/mois",
    features: ["Tout Plus +", "Espace de travail partagé", "Données non utilisées pour entraînement", "Console admin"],
    recommended: false
  },
  {
    name: "ChatGPT Enterprise",
    price: "Sur devis",
    features: ["Tout Team +", "SSO/SCIM", "Fenêtre contexte illimitée", "Analytics avancés", "SLA garanti"],
    recommended: false
  }
];

const FormationChatGPTEntreprisePage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Formation ChatGPT Entreprise | Maîtrisez l'IA OpenAI | Académie IA</title>
        <meta name="description" content="Formation ChatGPT pour entreprise : maîtrisez GPT-4, Custom GPTs, DALL-E 3. Formation pratique avec cas concrets. Devis gratuit." />
        <meta name="keywords" content="formation ChatGPT, ChatGPT entreprise, formation GPT-4, Custom GPTs formation, ChatGPT Plus Team Enterprise" />
        <link rel="canonical" href="https://academie-ia.fr/formation-chatgpt-entreprise" />
        <meta property="og:title" content="Formation ChatGPT Entreprise" />
        <meta property="og:description" content="Maîtrisez ChatGPT et ses fonctionnalités avancées pour votre entreprise." />
      </Helmet>

      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full text-green-400 text-sm font-medium mb-6">
              Formation OpenAI
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6">
              Formation{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                ChatGPT
              </span>{" "}
              Entreprise
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              ChatGPT n'est pas qu'un chatbot. C'est un assistant capable de transformer votre productivité — 
              si vous savez l'utiliser correctement.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/tunnel"
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-xl"
              >
                <Zap className="w-5 h-5" />
                Demander une formation
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-4 bg-green-500 text-white font-bold rounded-xl"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp direct
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What ChatGPT Can Do */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ce que ChatGPT peut faire pour votre entreprise
            </h2>
            <p className="text-xl text-gray-400">
              Au-delà du simple chat, un écosystème complet d'outils IA
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                </div>
                <p className="text-gray-400 mb-4">{feature.description}</p>
                <div className="flex flex-wrap gap-2">
                  {feature.examples.map((example, j) => (
                    <span key={j} className="text-xs bg-green-500/10 text-green-400 px-3 py-1 rounded-full">
                      {example}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Comparison */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Quelle version de ChatGPT pour votre entreprise ?
            </h2>
            <p className="text-xl text-gray-400">
              Nous vous aidons à choisir et à maîtriser la bonne formule
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative bg-white/5 border rounded-2xl p-6 ${
                  plan.recommended 
                    ? 'border-green-500/50 ring-2 ring-green-500/20' 
                    : 'border-white/10'
                }`}
              >
                {plan.recommended && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                    Recommandé
                  </span>
                )}
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="text-2xl font-black text-green-400 mb-4">{plan.price}</div>
                <ul className="space-y-2">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900/50 to-[#050505]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ce que vous apprendrez
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Rédiger des prompts efficaces (prompt engineering)",
              "Créer des Custom GPTs pour vos tâches récurrentes",
              "Utiliser DALL-E 3 pour la création visuelle",
              "Exploiter Code Interpreter pour l'analyse de données",
              "Intégrer ChatGPT à vos workflows existants",
              "Respecter les bonnes pratiques de confidentialité",
              "Éviter les erreurs courantes (hallucinations, biais)",
              "Mesurer et optimiser vos gains de productivité"
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4"
              >
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-white">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-900/20 to-emerald-900/20 border-y border-white/10">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: Clock, stat: "10h", desc: "gagnées par semaine en moyenne" },
              { icon: Users, stat: "200M+", desc: "utilisateurs dans le monde" },
              { icon: TrendingUp, stat: "x5", desc: "vitesse de production de contenu" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col items-center"
              >
                <item.icon className="w-8 h-8 text-green-400 mb-2" />
                <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 mb-1">
                  {item.stat}
                </div>
                <p className="text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à maîtriser ChatGPT ?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              En une journée, vos équipes passent de "j'ai testé ChatGPT" à "je l'utilise pour tout".
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/formation-ia"
                className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xl font-bold rounded-2xl"
              >
                <Zap className="w-6 h-6" />
                Réserver une formation
                <ArrowRight className="w-6 h-6" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/10">
        <p className="text-gray-500 text-sm">
          Académie IA © {new Date().getFullYear()} — Formation Intelligence Artificielle
        </p>
      </footer>
    </div>
  );
};

export default FormationChatGPTEntreprisePage;
